<template lang="pug">
AppDialog.referral-dialog(
  :key="show"
  append-to-body
  :show="show"
  :loading="showDialogLoading"
  title="Referrals & Discounts"
  @close="close"
)
  .form-group
    label.control-label
      | How it works
    .referral-info.referral-markdown(v-html="program.description")
  .referral-fields
    .form-group(v-if="showOrganizationDropdown")
      label.control-label
        | Referring Organization
        AppTooltipIcon(
          type="info-filled"
          :size="12"
          class="ml-4"
          message="Choose which organization you want the link (and resulting discounts) to apply to"
        )
      AppSelect(
        v-model="currentOrganizationId"
        :options="organizationOptions"
        :select2-options="select2Options"
      )
    .referral-blocked.referral-markdown(
      v-if="showBlockedInfo"
      v-html="program.blocked_info"
    )
    .form-group(v-if="showReferralLink")
      label.control-label
        | Your referral link
        AppTooltipIcon(
          type="info-filled"
          :size="12"
          class="ml-4"
          message="When new members subscribe using your link, you’ll earn a discount"
        )
      AppCopyInputValue(
        placeholder="Custom link will appear when you select an organization"
        button-text="Copy link"
        :value="program.tracking_url || ''"
      )
  .referral-share-buttons(v-if="showShareButtons")
    .form-group
      label.control-label
        | Share
      .buttons
        a.btn.btn-sm.btn-default(
          v-for="(button, index) in shareButtons"
          :key="`${button.label}-${index}`"
          :class="`share-${button.type}`"
          target="_blank"
          :href="button.href"
        )
          i(:class="button.iconClasses")
          | {{ button.label }}
  .referral-discounts
    label.control-label(v-if="showReferralList || showReferralSummary")
      | Referral discounts
    .referral-summary(
      v-if="showReferralSummary"
      :class="referralSummaryClass"
    )
      | {{ referralSummary }}
    .referral-share-link(v-if="showShareYourLink")
      i.fa.fa-lightbulb-o
      |
      | Share your link then come back here and track your progress
    .referral-list(v-if="showReferralList")
      AppInfiniteScroll(
        max-height="25em"
        @loadmore="loadMore"
      )
        template(#loadMore)
          .load-more(v-if="hasMoreReferrals")
            a(@click.prevent="loadMore") {{ loadMoreText }}
        table.table
          colgroup
            col
            col(style="width: 14em")
          tr(
            v-for="referral in referralsWithInfo"
            :key="referral.conversion_id"
          )
            td.detail
              span.summary {{ referral.info.summary }}
              span.changed {{ referral.info.date }}
            td.note(:class="referral.info.note_class")
              | {{ referral.info.note }}
              AppTooltipIcon(
                v-tooltip="{ placement: 'auto bottom' }"
                placement="top"
                type="info-filled"
                :size="12"
                class="ml-4"
                :message="referral.info.note_tooltip"
              )
  .referral-upgrade-plan(v-if="showUpgradePlan")
    a.btn.btn-primary(:href="organizationPlansRoute") Upgrade to get started
    .info
      | Referral program only available for owned organizations on paid plans
  .referral-new-organization(v-if="showCreateOrganization")
    a.btn.btn-primary(:href="newOrganizationRoute") Create an organization to get started
    .info
      | Referral program only available for owned organizations on paid plans
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { OrganizationsResource } from '~/resources';

export default {
  name: 'ReferralDialog',
  data: () => ({
    show: false,
    select2Options: Object.freeze({
      placeholder: 'Select organization',
      width: '100%',
    }),
  }),
  computed: {
    ...mapState(['organization_id', 'program']),
    ...mapGetters([
      'showDialogLoading',
      'showLoadingMore',
      'hasMoreReferrals',
      'hasOneOrganization',
      'organizationOptions',

      'oldestSignupTime',
      'referralsWithInfo',
      'referralSummary',
      'referralSummaryClass',

      'showOrganizationDropdown',
      'showCreateOrganization',
      'showShareButtons',
      'showReferralList',
      'showReferralLink',
      'showReferralSummary',
      'showShareYourLink',
      'showUpgradePlan',
      'showBlockedInfo',

      'shareButtons',
    ]),
    // region Organization select control
    currentOrganizationId: {
      get() {
        return this.organization_id;
      },
      set(value) {
        this.setCurrentOrganization({
          organizationId: value,
          activate: value !== 'auto',
          load: ['referrals', 'program'],
        });
      },
    },
    // endregion

    // region Infinite loading
    loadMoreText() {
      return this.showLoadingMore ? 'Loading...' : 'Show more';
    },
    // endregion

    newOrganizationRoute() {
      return OrganizationsResource.wizardLink();
    },
    organizationPlansRoute() {
      return OrganizationsResource.plansLink({ id: this.currentOrganizationId });
    },
  },
  mounted() {
    const params = new URL(window.location).searchParams;
    const openReferral = params.get('openReferral');

    if (openReferral) {
      this.open(openReferral);
    }
  },
  methods: {
    ...mapActions(['loadDialogData', 'resetDialogState', 'setCurrentOrganization']),
    // region Main dialog open/close
    open(organizationId = 'auto') {
      this.show = true;
      organizationId = organizationId || 'auto';
      this.setCurrentOrganization({
        organizationId,
        activate: organizationId !== 'auto',
        load: ['organizations', 'referrals', 'program'],
      }).then(() => {
        if (organizationId === 'auto' && this.hasOneOrganization) {
          this.setCurrentOrganization({
            organizationId: this.organizationOptions[0].id,
            activate: true,
            load: ['referrals', 'program'],
          });
        }
      });
    },
    close() {
      this.show = false;
      this.resetDialogState();
    },
    // endregion

    // region infinite scrolling
    loadMore() {
      if (this.hasMoreReferrals) {
        this.loadDialogData({ load: ['referrals'], offset: this.oldestSignupTime });
      }
    },
    // endregion
  },
};
</script>
